import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useConsentGuard } from './TrackingEnforcer';
import {
  FileText, Target, Flame, TrendingUp, Link2, Globe,
  BarChart3, ArrowRight, ExternalLink, Sparkles, Mail, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [trends, setTrends] = useState([]);
  const [platforms, setPlatforms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      axios.get(`${API}/analytics/dashboard`, { headers }),
      axios.get(`${API}/articles`, { headers }),
      axios.get(`${API}/analytics/content-trends`, { headers }),
      axios.get(`${API}/analytics/platform-breakdown`, { headers }),
    ]).then(([dashRes, artRes, trendRes, platRes]) => {
      setData(dashRes.data);
      setArticles(artRes.data);
      setTrends(trendRes.data);
      setPlatforms(platRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  const published = articles.filter(a => a.status === 'published');
  const drafts = articles.filter(a => a.status === 'draft');
  const avgSeo = data?.seo?.average_score || 0;
  const seoExcellent = articles.filter(a => (a.seo_score || 0) >= 80).length;
  const seoGood = articles.filter(a => (a.seo_score || 0) >= 50 && (a.seo_score || 0) < 80).length;
  const seoNeeds = articles.filter(a => (a.seo_score || 0) > 0 && (a.seo_score || 0) < 50).length;
  const maxTrend = Math.max(...trends.map(t => t.articles), 1);

  return (
    <div className="fade-in" data-testid="analytics-page">
      <div className="mb-6">
        <h1 className="font-serif text-3xl mb-1">Analytics</h1>
        <p className="text-sm text-[var(--color-muted)]">Your content activity, output trends, and growth insights.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={FileText} label="Total Articles" value={articles.length} sub={`${published.length} published · ${drafts.length} drafts`} color="#E8735C" />
        <StatCard icon={Target} label="Avg SEO Score" value={avgSeo.toFixed(0)} sub={avgSeo >= 70 ? 'Excellent' : avgSeo >= 50 ? 'Good' : 'Needs work'} color="var(--color-charcoal)" />
        <StatCard icon={Flame} label="Current Streak" value={data?.streak?.current || 0} sub={`Best: ${data?.streak?.longest || 0} days`} color="#E8735C" />
        <StatCard icon={TrendingUp} label="Credits Used" value={(data?.credits?.used || 0).toFixed(1)} sub={`${(data?.credits?.remaining || 0).toFixed(1)} remaining`} color="var(--color-charcoal)" />
      </div>

      {/* Weekly Trends + SEO */}
      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="card p-6">
          <h2 className="font-serif text-lg mb-1">Content Output</h2>
          <p className="text-[10px] text-[var(--color-muted)] mb-5">Articles created per week (last 12 weeks)</p>
          <div className="flex items-end gap-1.5" style={{ height: 160 }}>
            {trends.map((t, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                {t.articles > 0 && <span className="text-[9px] font-semibold text-[var(--color-charcoal)]">{t.articles}</span>}
                <div className="w-full rounded-md" style={{ height: `${Math.max((t.articles / maxTrend) * 120, 3)}px`, background: t.articles > 0 ? 'var(--color-coral)' : 'var(--color-border)', opacity: t.articles > 0 ? 1 : 0.25 }} />
                <span className="text-[8px] text-[var(--color-muted)] -rotate-45 origin-top-left whitespace-nowrap">{t.week}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-6">
          <h2 className="font-serif text-lg mb-1">SEO Score Distribution</h2>
          <p className="text-[10px] text-[var(--color-muted)] mb-5">How your articles rank by SEO quality</p>
          <div className="space-y-4">
            <ScoreBar label="Excellent (80+)" count={seoExcellent} total={articles.length} color="#22c55e" />
            <ScoreBar label="Good (50-79)" count={seoGood} total={articles.length} color="var(--color-coral)" />
            <ScoreBar label="Needs Work (<50)" count={seoNeeds} total={articles.length} color="var(--color-border)" />
          </div>
          {articles.length === 0 && <p className="text-xs text-[var(--color-muted)] text-center mt-8">Create articles to see SEO distribution.</p>}
        </div>
      </div>

      {/* Publishing + Platforms + Brand */}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div className="card p-6">
          <h3 className="font-display text-sm font-medium mb-4">Content Status</h3>
          <div className="space-y-3">
            <StatusRow label="Published" count={published.length} color="#22c55e" />
            <StatusRow label="Drafts" count={drafts.length} color="var(--color-muted)" />
            <StatusRow label="Scheduled" count={articles.filter(a => a.status === 'scheduled').length} color="var(--color-coral)" />
          </div>
          <div className="mt-5 pt-4 border-t border-[var(--color-border)]">
            <h4 className="text-[10px] font-medium uppercase text-[var(--color-muted)] mb-2">Publishing</h4>
            <div className="space-y-2">
              <StatusRow label="Total Scheduled" count={data?.publishing?.total_scheduled || 0} color="var(--color-charcoal)" />
              <StatusRow label="Published to Platforms" count={data?.publishing?.published || 0} color="#22c55e" />
              <StatusRow label="Pending" count={data?.publishing?.pending || 0} color="var(--color-coral)" />
            </div>
          </div>
        </div>
        <div className="card p-6">
          <h3 className="font-display text-sm font-medium mb-4">Connected Platforms</h3>
          {(platforms?.connected_platforms || []).length === 0 ? (
            <div className="text-center py-6">
              <Link2 className="w-6 h-6 text-[var(--color-muted)] mx-auto mb-2" />
              <p className="text-xs text-[var(--color-muted)]">No platforms connected yet.</p>
              <p className="text-[10px] text-[var(--color-muted)] mt-1">Connect Facebook, LinkedIn, or WordPress to track publishing.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(platforms?.connected_platforms || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-[var(--color-surface)]">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-[rgba(232,115,92,0.08)]">
                      <Globe className="w-3 h-3 text-[var(--color-coral)]" />
                    </div>
                    <span className="text-xs font-medium">{p.site_name || p.platform}</span>
                  </div>
                  <span className="text-[10px] text-[var(--color-muted)] capitalize">{p.platform}</span>
                </div>
              ))}
            </div>
          )}
          {(platforms?.platform_posts || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--color-border)]">
              <h4 className="text-[10px] font-medium uppercase text-[var(--color-muted)] mb-2">Posts per Platform</h4>
              {platforms.platform_posts.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1">
                  <span className="text-xs capitalize">{p.platform}</span>
                  <span className="text-xs font-medium">{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card p-6">
          <h3 className="font-display text-sm font-medium mb-4">Brand & Tools</h3>
          <div className="space-y-3">
            <StatusRow label="Brand Voices Created" count={data?.brand?.voices || 0} color="#8B5CF6" />
            <StatusRow label="Brand Reports Generated" count={data?.brand?.reports || 0} color="#8B5CF6" />
            <StatusRow label="Integrations Connected" count={data?.integrations_count || 0} color="var(--color-coral)" />
          </div>
        </div>
      </div>

      {/* Top Articles */}
      <div className="card p-6 mb-6">
        <h3 className="font-display text-sm font-medium mb-4">Top Articles by SEO Score</h3>
        {articles.length === 0 ? (
          <p className="text-xs text-[var(--color-muted)] text-center py-8">No articles yet. Create your first to see rankings.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-2">
            {[...articles].sort((a, b) => (b.seo_score || 0) - (a.seo_score || 0)).slice(0, 6).map((article) => (
              <div key={article.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-surface)]">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0" style={{ background: (article.seo_score || 0) >= 70 ? 'rgba(34,197,94,0.1)' : 'rgba(232,115,92,0.1)', color: (article.seo_score || 0) >= 70 ? '#16a34a' : 'var(--color-coral)' }}>
                  {article.seo_score || 0}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{article.title || 'Untitled'}</p>
                  <p className="text-[10px] text-[var(--color-muted)]">{new Date(article.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`badge badge-${article.status} flex-shrink-0`}>{article.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Google Analytics */}
      <GoogleAnalyticsCard />
    </div>
  );
};

// ============== GOOGLE ANALYTICS ==============
const GoogleAnalyticsCard = () => {
  const { getAuthHeaders } = useAuth();
  const { isAllowed: analyticsConsented, requestConsent } = useConsentGuard('analytics');
  const [gaStatus, setGaStatus] = useState(null);
  const [gaMetrics, setGaMetrics] = useState(null);
  const [gaProperties, setGaProperties] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingProps, setLoadingProps] = useState(false);
  const [showPropertyPicker, setShowPropertyPicker] = useState(false);
  const [period, setPeriod] = useState('30');
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchGaStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('ga_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchGaStatus = async () => {
    try {
      const res = await axios.get(`${API}/analytics/ga/status`, { headers: getAuthHeaders() });
      setGaStatus(res.data);
      if (res.data.connected && res.data.property_id) fetchMetrics('30');
      else if (res.data.connected && !res.data.property_id) fetchProperties();
    } catch { setGaStatus({ connected: false }); }
    finally { setLoadingStatus(false); }
  };

  const fetchMetrics = async (days) => {
    setLoadingMetrics(true);
    try {
      const res = await axios.get(`${API}/analytics/ga/metrics?period=${days}`, { headers: getAuthHeaders() });
      setGaMetrics(res.data);
    } catch (err) { if (err.response?.status === 401) setGaStatus({ connected: false }); }
    finally { setLoadingMetrics(false); }
  };

  const fetchProperties = async () => {
    setLoadingProps(true);
    setShowPropertyPicker(true);
    try {
      const res = await axios.get(`${API}/analytics/ga/properties`, { headers: getAuthHeaders() });
      setGaProperties(res.data);
    } catch { }
    finally { setLoadingProps(false); }
  };

  const handleConnect = async () => {
    if (!analyticsConsented) {
      requestConsent();
      return;
    }
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/analytics/ga/connect`, { headers: getAuthHeaders() });
      window.location.href = res.data.auth_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start Google connection');
      setConnecting(false);
    }
  };

  const handleSelectProperty = async (prop) => {
    try {
      await axios.post(`${API}/analytics/ga/select-property`, { property_id: prop.property_id, property_name: prop.property_name }, { headers: getAuthHeaders() });
      setShowPropertyPicker(false);
      setGaStatus(prev => ({ ...prev, property_id: prop.property_id, property_name: prop.property_name }));
      fetchMetrics('30');
    } catch { }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Analytics?')) return;
    try {
      await axios.delete(`${API}/analytics/ga/disconnect`, { headers: getAuthHeaders() });
      setGaStatus({ connected: false });
      setGaMetrics(null);
    } catch { }
  };

  const handlePeriodChange = (d) => { setPeriod(d); fetchMetrics(d); };
  const fmtDuration = (s) => { if (!s) return '0s'; const m = Math.floor(s / 60); return m > 0 ? `${m}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`; };
  const fmtNum = (n) => { if (!n) return '0'; if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toLocaleString(); };

  if (loadingStatus) return null;
  const isLive = gaStatus?.connected && gaStatus?.property_id;

  return (
    <div className="card overflow-hidden" data-testid="ga-section">
      <div className="p-6 relative" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1f2f 50%, #1a1a1a 100%)' }}>
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(232,115,92,0.4), transparent 60%)' }} />
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-white mb-1">Website Performance</h3>
              <p className="text-xs text-white/50">{isLive ? `${gaStatus.property_name} · Last ${period} days` : 'Connect Google Analytics to see real traffic data'}</p>
            </div>
          </div>
          {isLive && (
            <div className="flex items-center gap-2">
              {['7','30','90'].map(d => (
                <button key={d} onClick={() => handlePeriodChange(d)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-all ${period === d ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'}`} data-testid={`ga-period-${d}`}>{d}d</button>
              ))}
              <button onClick={handleDisconnect} className="text-[10px] px-2 py-1 text-white/30 hover:text-white/60 ml-2">Disconnect</button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Not connected */}
        {!gaStatus?.connected && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(232,115,92,0.06)' }}>
              <BarChart3 className="w-7 h-7 text-[var(--color-coral)]" />
            </div>
            <h4 className="font-display text-base font-medium mb-2">Connect Google Analytics</h4>
            <p className="text-xs text-[var(--color-muted)] mb-5 max-w-md mx-auto">See real pageviews, visitors, top-performing content, and traffic sources directly in your dashboard.</p>
            <button onClick={handleConnect} disabled={connecting} className="btn btn-primary text-sm" data-testid="ga-connect-btn">
              {connecting ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting...</> : <><ExternalLink className="w-4 h-4" /> Connect Google Analytics</>}
            </button>
          </div>
        )}

        {/* Property picker */}
        {gaStatus?.connected && !gaStatus?.property_id && showPropertyPicker && (
          <div>
            <h4 className="font-display text-sm font-medium mb-3">Select a GA4 Property</h4>
            {loadingProps ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[var(--color-muted)]" /></div>
            ) : gaProperties.length === 0 ? (
              <p className="text-xs text-[var(--color-muted)] py-4">No GA4 properties found. Make sure your Google account has access to at least one GA4 property.</p>
            ) : (
              <div className="space-y-2">
                {gaProperties.map((prop, i) => (
                  <button key={i} onClick={() => handleSelectProperty(prop)} className="w-full text-left p-3 rounded-xl bg-[var(--color-surface)] hover:bg-[rgba(232,115,92,0.04)] transition-colors" data-testid={`ga-property-${prop.property_id}`}>
                    <p className="text-xs font-medium">{prop.property_name}</p>
                    <p className="text-[10px] text-[var(--color-muted)]">{prop.account_name} · ID: {prop.property_id}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Live metrics */}
        {isLive && (loadingMetrics ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[var(--color-muted)]" /></div>
        ) : gaMetrics && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ga-overview-metrics">
              <GaMetricCard label="Pageviews" value={fmtNum(gaMetrics.overview?.pageviews)} />
              <GaMetricCard label="Total Users" value={fmtNum(gaMetrics.overview?.total_users)} />
              <GaMetricCard label="New Users" value={fmtNum(gaMetrics.overview?.new_users)} />
              <GaMetricCard label="Avg. Session" value={fmtDuration(gaMetrics.overview?.avg_session_duration)} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <GaMetricCard label="Sessions" value={fmtNum(gaMetrics.overview?.sessions)} />
              <GaMetricCard label="Bounce Rate" value={`${((gaMetrics.overview?.bounce_rate || 0) * 100).toFixed(1)}%`} />
              <GaMetricCard label="Property" value={gaMetrics.property_name || gaMetrics.property_id} small />
            </div>

            {/* Daily trend */}
            {gaMetrics.daily_trend?.length > 0 && (
              <div>
                <h4 className="font-display text-sm font-medium mb-3">Daily Pageviews</h4>
                <div className="flex items-end gap-0.5" style={{ height: 120 }}>
                  {(() => {
                    const mx = Math.max(...gaMetrics.daily_trend.map(d => d.pageviews), 1);
                    return gaMetrics.daily_trend.map((d, i) => (
                      <div key={i} className="flex-1 group relative">
                        <div className="w-full rounded-sm hover:opacity-80" style={{ height: `${Math.max((d.pageviews / mx) * 100, 2)}px`, background: 'var(--color-coral)', opacity: d.pageviews > 0 ? 0.7 : 0.15 }} />
                        <div className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[var(--color-charcoal)] text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">{d.date}: {d.pageviews} views</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Top pages + sources */}
            <div className="grid lg:grid-cols-2 gap-5">
              {gaMetrics.top_pages?.length > 0 && (
                <div>
                  <h4 className="font-display text-sm font-medium mb-3">Top Pages</h4>
                  <div className="space-y-1.5">
                    {gaMetrics.top_pages.slice(0, 8).map((pg, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-xl bg-[var(--color-surface)]">
                        <span className="text-xs truncate flex-1 mr-3">{pg.page_title || '(not set)'}</span>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-[10px] text-[var(--color-muted)]">{pg.users} users</span>
                          <span className="text-xs font-medium">{fmtNum(pg.pageviews)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {gaMetrics.traffic_sources?.length > 0 && (
                <div>
                  <h4 className="font-display text-sm font-medium mb-3">Traffic Sources</h4>
                  <div className="space-y-2">
                    {(() => {
                      const mx = Math.max(...gaMetrics.traffic_sources.map(s => s.sessions), 1);
                      return gaMetrics.traffic_sources.map((src, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-xs mb-1"><span>{src.channel}</span><span className="font-medium">{fmtNum(src.sessions)}</span></div>
                          <div className="h-1.5 rounded-full bg-[var(--color-surface)]"><div className="h-full rounded-full" style={{ width: `${(src.sessions / mx) * 100}%`, background: 'var(--color-coral)', opacity: 0.7 }} /></div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Coaching message */}
        <div className={`rounded-2xl p-5 bg-[var(--color-surface)] ${isLive && gaMetrics ? 'mt-6' : ''}`}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-[rgba(232,115,92,0.08)] flex-shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-[var(--color-coral)]" />
            </div>
            <div>
              <h4 className="font-display text-sm font-medium mb-2">Content performance takes time — here's what to do next</h4>
              <p className="text-xs text-[var(--color-muted)] leading-relaxed mb-3">Organic results compound over time — most content takes <strong>4-8 weeks</strong> to start ranking and gaining traction.</p>
              <p className="text-xs text-[var(--color-muted)] leading-relaxed mb-4">We recommend running a <strong>branded search campaign on Google Ads</strong> to accelerate visibility while organic rankings develop.</p>
              <a href="/contact" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--color-coral)] text-white text-xs font-medium hover:opacity-90 transition-opacity">
                <Mail className="w-3 h-3" /> Contact us for campaign help <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-5 grid md:grid-cols-3 gap-3">
          <TipCard title="Publish consistently" desc="Aim for 2-3 articles per week. Consistency signals authority to search engines." stat="2-3x" statLabel="per week" />
          <TipCard title="Share on social" desc="Repurpose articles to Facebook, LinkedIn, and Instagram to drive initial traffic." stat="5x" statLabel="more reach" />
          <TipCard title="Build backlinks" desc="Each published article is a link-building opportunity. Guest post, comment, and engage." stat="4-8" statLabel="weeks to rank" />
        </div>
      </div>
    </div>
  );
};

// ============== HELPERS ==============
const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="stat-card">
    <div className="flex items-start justify-between mb-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--color-surface)]"><Icon className="w-4 h-4" style={{ color }} /></div>
    </div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
    <p className="text-[11px] text-[var(--color-muted)] mt-1">{sub}</p>
  </div>
);

const ScoreBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span>{label}</span><span className="font-medium">{count}</span></div>
      <div className="h-1.5 rounded-full bg-[var(--color-surface)]"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
};

const StatusRow = ({ label, count, color }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /><span className="text-xs">{label}</span></div>
    <span className="text-xs font-medium">{count}</span>
  </div>
);

const GaMetricCard = ({ label, value, small }) => (
  <div className="p-4 rounded-xl bg-[var(--color-surface)] text-center">
    <div className={`font-bold text-[var(--color-charcoal)] mb-1 ${small ? 'text-sm' : 'text-2xl'}`}>{value}</div>
    <p className="text-[10px] text-[var(--color-muted)]">{label}</p>
  </div>
);

const TipCard = ({ title, desc, stat, statLabel }) => (
  <div className="p-4 rounded-xl bg-[var(--color-surface)]">
    <div className="flex items-baseline gap-1 mb-2"><span className="text-lg font-bold text-[var(--color-coral)]">{stat}</span><span className="text-[10px] text-[var(--color-muted)]">{statLabel}</span></div>
    <h4 className="text-xs font-medium mb-1">{title}</h4>
    <p className="text-[10px] text-[var(--color-muted)] leading-relaxed">{desc}</p>
  </div>
);

export default Analytics;
